/* eslint-disable @nx/enforce-module-boundaries */
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { QRCodeComponent } from 'angularx-qrcode';
import { HttpClient } from '@angular/common/http';
import { ClubSubscriberStatusResponse } from 'apps/account-linker/src/backend/controller/model/club-suscriber-checker.ts/club-subscriber-checker';
import { firstValueFrom } from 'rxjs';
import { sleep } from '../../utils/sleep-util';
import {
  DeepLinkOpenerService,
  DeepLinkOpeningStatus,
} from '../../services/deeplink-opener.service';
import { LoaderComponent } from '../shared/loader/loader.component';
import { AssetsUrlPipe } from '../../pipes/assets-url.pipe';
import { NewRelicService } from '../../services/newrelic.service';
import { RequestDataService } from '../../services/request-data.service';
import { DeviceType } from 'apps/account-linker/src/backend/model/request-data';

@Component({
  selector: 'al-clube-subscriber-checker',
  imports: [CommonModule, QRCodeComponent, LoaderComponent, AssetsUrlPipe],
  standalone: true,
  templateUrl: './clube-subscriber-checker.component.html',
  styleUrls: ['./clube-subscriber-checker.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ClubeSubscriberCheckerComponent
  implements AfterViewInit, OnDestroy
{
  @ViewChild('qrElem', { read: ElementRef }) qrElem!: ElementRef;

  // Injections.
  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  private assetsUrlPipe = inject(AssetsUrlPipe);
  private deeplinkOpenerService = inject(DeepLinkOpenerService);
  private newrelicService = inject(NewRelicService);
  private requestDataService = inject(RequestDataService);

  // Static properties.
  public static errorMessages = {
    title: 'Não encontramos seu Clube iFood',
    subtitle:
      'Verifique o status da sua assinatura no iFood ou tente novamente.',
  };

  private static campaignName = 'club_promo_october_2025';
  private static timeToStartPooling = 7000; // in ms
  private static poolingInterval = 3000; // in ms
  private static timeBeforeAutomaticLinkoff = 4000; // in ms
  private static maxPollingAttempts = 50;

  // Public instance properties.
  public readonly deepLinkOpeningStatusEnum = DeepLinkOpeningStatus;

  public isLoading = true;
  public finalQr: string | null = null;
  public qrData?: string;
  public title = 'Boa! Quem tem Clube iFood viaja mais barato';
  public subtitle =
    'Escaneie o QR Code para acessar o iFood e aproveitar todos os seus benefícios!';

  public actionButtonText = 'Tentar novamente';

  public deviceType: DeviceType | null = null;
  public hasError = signal(false);
  public linkOffStatus = signal<DeepLinkOpeningStatus>(
    DeepLinkOpeningStatus.idle
  );

  // Private instance properties.
  private continuePolling = false;
  private sinceTimestamp = new Date().toISOString();
  private successPageUri = 'www.decolar.com/ofertas-viagens/hoteisclubeifood';
  private successPageUrl = `https://${this.successPageUri}`;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const isWebView = this.requestDataService.isWebView();
      if (this.requestDataService.isClubeMember()) {
        window.location.href = this.successPageUrl;
        return;
      }
      const trackerId = this.requestDataService.getTrackerId();
      this.deviceType = this.requestDataService.getDeviceType();
      computed(() => {
        const actualStatus = this.linkOffStatus();
        if (
          actualStatus === DeepLinkOpeningStatus.error ||
          actualStatus === DeepLinkOpeningStatus.blocked
        ) {
          this.hasError.set(true);
        }
      });
      effect(() => {
        if (this.hasError()) {
          this.title = ClubeSubscriberCheckerComponent.errorMessages.title;
          this.subtitle =
            ClubeSubscriberCheckerComponent.errorMessages.subtitle;
        }
      });
      const linkOffUrl = this.getDeepLink(
        isWebView,
        this.deviceType,
        trackerId
      );

      if (this.deviceType === DeviceType.MOBILE) {
        setTimeout(() => {
          this.newrelicService.setCustomAttribute(
            'validation_result',
            'deeplink_open_attempted'
          );
          this.deeplinkOpenerService
            .openInSameTab(linkOffUrl, this.linkOffStatus)
            .then(() => {
              // If this point is reached, the deeplink did not open the app
              this.hasError.set(true);
              this.newrelicService.setCustomAttribute(
                'validation_result',
                'deeplink_not_opened'
              );
            });
        }, ClubeSubscriberCheckerComponent.timeBeforeAutomaticLinkoff);
      } else {
        this.qrData = linkOffUrl;
        this.isLoading = false;
        setTimeout(() => {
          this.startCheckerPooling();
        }, ClubeSubscriberCheckerComponent.timeToStartPooling);
      }
    }
  }

  async ngAfterViewInit() {
    if (
      isPlatformBrowser(this.platformId) &&
      this.deviceType !== DeviceType.MOBILE
    ) {
      setTimeout(() => {
        this.renderWithLogo();
      }, 50);
    }
  }

  handleLinkOpening() {
    const status = this.linkOffStatus();
    if (status === DeepLinkOpeningStatus.error) {
      return this.handleError();
    }
    if (status === DeepLinkOpeningStatus.success) {
      window.location.href = this.successPageUrl;
    }
  }

  handleError() {
    window.document.location.reload();
  }

  private renderWithLogo() {
    const qrCanvas = this.qrElem.nativeElement.querySelector('canvas');
    const size = qrCanvas.width;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Cannot render QR with logo: unable to get canvas context');
      this.finalQr = qrCanvas.toDataURL('image/png');
      return;
    }

    // Dibujar QR original
    ctx.drawImage(qrCanvas, 0, 0);

    // Cargar logo
    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.src = this.assetsUrlPipe.transform('images/ifood_logo.svg');

    logo.onload = () => {
      const boxSize = size * 0.2; // cuadrado central (30%)
      const x = (size - boxSize) / 2;
      const y = (size - boxSize) / 2;

      // 3) Fondo cuadrado blanco
      ctx.fillStyle = '#4300D2';
      ctx.fillRect(x, y, boxSize, boxSize);

      // 4) Ajustar logo proporcionalmente dentro del cuadro
      const logoRatio = logo.width / logo.height;
      const finalW = boxSize * 0.9;
      const finalH = finalW / logoRatio;

      const logoX = x + (boxSize - finalW) / 2;
      const logoY = y + (boxSize - finalH) / 2;

      // 5) Dibujar el logo dentro del cuadrado
      ctx.drawImage(logo, logoX, logoY, finalW, finalH);

      this.finalQr = canvas.toDataURL('image/png');
      this.qrElem.nativeElement.innerHTML = '';
    };
  }

  private async startCheckerPooling() {
    let polledTimes = 0;
    this.continuePolling = true;
    while (this.continuePolling) {
      const pollingResponse = await this.pollRequest();
      polledTimes++;
      if (pollingResponse.found) {
        this.continuePolling = false;
        if (pollingResponse.isIfoodClubMember) {
          this.newrelicService.setCustomAttribute(
            'validation_result',
            'successful'
          );
          window.location.href = this.successPageUrl;
        } else {
          this.newrelicService.setCustomAttribute(
            'validation_result',
            'not_member'
          );
          this.hasError.set(true);
        }
      } else {
        if (polledTimes >= ClubeSubscriberCheckerComponent.maxPollingAttempts) {
          this.continuePolling = false;
          this.newrelicService.setCustomAttribute(
            'validation_result',
            'timeout'
          );
          this.hasError.set(true);
        } else {
          await sleep(ClubeSubscriberCheckerComponent.poolingInterval);
        }
      }
    }
  }

  private async pollRequest(): Promise<ClubSubscriberStatusResponse> {
    try {
      return await firstValueFrom(
        this.http.get<ClubSubscriberStatusResponse>(
          `/account-linker/api/ifood/club-subscriber-checker`,
          {
            params: {
              since: this.sinceTimestamp,
            },
          }
        )
      );
    } catch (error) {
      console.error('Error checking club subscriber status:', error);
      return { found: false };
    }
  }

  private getDeepLink(
    isNativeApp: boolean,
    deviceType: string,
    trackerId: string
  ): string {
    const platformForDeeplink = isNativeApp
      ? 'app'
      : deviceType === 'mobile'
      ? 'web-mobile'
      : 'web-desktop';
    const pageQueryParam = new URLSearchParams();
    if (platformForDeeplink !== 'web-desktop') {
      pageQueryParam.set('url', this.successPageUri);
    }
    pageQueryParam.set('tracker_id', trackerId);
    pageQueryParam.set('platform', platformForDeeplink);
    pageQueryParam.set(
      'campaign',
      ClubeSubscriberCheckerComponent.campaignName
    );
    pageQueryParam.set("source", "d_redirect_clube_checker");
    const pageParam = `https://disrupt.ifood.com.br/despegar/account/validate-club?${pageQueryParam.toString()}`;
    return `ifood://webmiddleware?isImmersive=true&isModal=false&isFullscreen=false&hideTopSafeArea=true&page=${encodeURIComponent(
      pageParam
    )}`;
  }

  ngOnDestroy() {
    this.continuePolling = false;
  }
}
